import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Button as AntButton, Spin } from 'antd';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { PlusSvg } from '../../assets/icons';
import CuriBookmarksSvg from '../../assets/icons/CuriBookmarks';
import FolderSvg from '../../assets/icons/FolderSvg';
import theme from '../../assets/theme';
import useAccessBadge from '../../hooks/useAccessBadge';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useDebounce from '../../hooks/useDebounce';
import useFolders from '../../hooks/useFolders';
import { useLocale } from '../../hooks/useLocale';
import { useMetrics } from '../../hooks/useMetrics';
import usePresentations, {
  currentPresentationsCacheKey,
} from '../../hooks/usePresentations';
import usePresentationsSearch from '../../hooks/usePresentationsSearch';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import useRouterMatch from '../../hooks/useRouterMatch';
import { initSortCondition, saveSortCondition } from '../../utils/sortUtils';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import LiveEmptyState from '../_molecules/LiveEmptyState';
import { SortDropdownOption } from '../_molecules/SortDropdown';
import { FoldersSection } from '../_organisms/FolderSection';
import { LessonsSection } from '../_organisms/LessonSection';
import {
  NewFolder,
  sortConditionToQuery,
  SortingControls,
} from '../_organisms/SortingControls';
import OnboardingModal from '../OnboardingModal';
import FolderPage from './FolderPage';

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  text-transform: uppercase;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;

export const NewLessonsPage = () => {
  const { parentFolderId } = useRouterMatch();
  useLocale();

  if (parentFolderId) {
    return <FolderPage />;
  } else {
    return <LessonsPage />;
  }
};

export default NewLessonsPage;

const SearchGrid = ({ search }: { search: string }) => {
  const { t } = useTranslate();
  const {
    presentations,
    total,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = usePresentationsSearch({
    isPrivate: true,
    search: search,
    parentFolderId: undefined,
  });

  const { ref: fetchMorePresentationsRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  return (
    <>
      <Title $fontSize={16} style={{ paddingLeft: 20 }}>
        <T keyName="searchResults" defaultValue="Search results" />{' '}
        <span style={{ fontSize: 14, marginLeft: 10 }}>
          {isFetching ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          ) : (
            `(${total} ${t('lessons', 'lessons')})`
          )}
        </span>
      </Title>
      <LessonsSection lessons={presentations} />
      <div ref={fetchMorePresentationsRef}>
        {isFetchingNextPage && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 50,
            }}
          >
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 20, color: theme.colors.black }}
                  spin
                />
              }
            />
          </div>
        )}
      </div>
      <div style={{ height: 20, width: '100%' }}>
        {/* Render this empty div to make sure the inView div triggers fetch more  */}
      </div>
    </>
  );
};

const LessonsPage = () => {
  useRoutePrefetch(`/[tenantId]/home`, undefined, {
    priority: true,
  });
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });

  const { t } = useTranslate();
  const isDesktop = useBreakPoint(800);
  const { parentFolderId, tenantId, router } = useRouterMatch();
  const { ref: fetchMorePresentationsRef, inView } = useInView();
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const [sortCondition, setSortCondition] = useState<SortDropdownOption>(
    initSortCondition(),
  );

  // Onboarding modal state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  // Show onboarding modal if user hasn't completed onboarding.
  // Skip when the page was opened with URL query params — that means the user
  // landed via a deep link (e.g. post-login redirect) and shouldn't be
  // interrupted.
  useEffect(() => {
    const hasUrlParams =
      typeof window !== 'undefined' && window.location.search.length > 0;
    if (userAccessBadge && !userAccessBadge.user.onboardingCompletedAt && !hasUrlParams) {
      setShowOnboardingModal(true);
    }
  }, [userAccessBadge]);

  const handleChangeSortCondition = (option: SortDropdownOption) => {
    setSortCondition(option);
    saveSortCondition(option);
  };

  const sortQuery = useMemo(
    () => ({ isPrivate: true, ...sortConditionToQuery(sortCondition) }),
    [sortCondition],
  );

  const presentationsCacheKey = useMemo(() => currentPresentationsCacheKey(sortQuery), [
    sortQuery,
  ]);

  const {
    presentations,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePresentations(sortQuery);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const {
    currentFolders,
    allFolders,
    isFetchingFolders,
    isDeletingFolder,
    deleteFolder,
    saveFolder,
    isSavingFolder,
  } = useFolders(parentFolderId);

  const [search, setSearch] = useState<string>();
  const debouncedSearchQuery = useDebounce(search, 500);

  const isLoadingData = isFetchingFolders || isFetching;

  const sortedFolders = useMemo(() => {
    return currentFolders.sort((a, b) => {
      if (!a.title || !b.title) return -1;
      if (sortCondition.value === 'alphaAsc') {
        return a.title?.localeCompare(b.title || '');
      } else if (sortCondition.value === 'alphaDesc') {
        return b.title?.localeCompare(a.title || '');
      } else if (sortCondition.value === 'newestModified') {
        return new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime();
      } else if (sortCondition.value === 'oldestModified') {
        return new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime();
      }
      return 0;
    });
  }, [currentFolders, sortCondition]);

  const isSearching = search !== undefined && search.length > 0;

  return (
    <>
      <Head>
        <title>{t('myLessons', 'My Lessons')}</title>
      </Head>
      <PageContainer>
        <ContentContainer $isDesktop={isDesktop}>
          <HeaderContainer>
            <HeaderTitle>
              <IconWrapper height="20px" width="20px">
                <FolderSvg />
              </IconWrapper>
              <span>
                <T keyName="myLessons" defaultValue="My Lessons" />
              </span>
            </HeaderTitle>
            <ControlsRow>
              <ButtonGroup>
                <CreateButton
                  icon={<PlusSvg />}
                  onClick={() => {
                    metrics.logEvent('ControlsRow.GetLessonClicked', {
                      from: 'lessonsPage',
                    });
                    router.push(`/${tenantId}/home`);
                  }}
                >
                  <T keyName="Get lesson" defaultValue="Get lesson" />
                  <span
                    style={{
                      fontSize: 12,
                      color: theme.colors.orange,
                      background: 'rgba(255,255,255,0.8)',
                      borderRadius: 7,
                      padding: '0 8px',
                      marginLeft: 10,
                    }}
                  >
                    <T keyName="ai" defaultValue="AI" />
                  </span>
                </CreateButton>
                <Link href={`/${tenantId}/lessons/new/edit`}>
                  <a>
                    <Button
                      height={'42px'}
                      type="ghost"
                      styles={{
                        paddingLeft: 16,
                        paddingRight: 16,
                        boxShadow: theme.effects.floatingCreatorElementShadow,
                      }}
                      onClick={() => {
                        metrics.logEvent('ControlsRow.NewFromBlankClicked', {
                          from: 'lessonsPage',
                        });
                      }}
                    >
                      <T keyName="newFromBlank" defaultValue="+ New from blank" />
                    </Button>
                  </a>
                </Link>
                <div
                  style={{ height: '42px', width: '1px', background: 'rgb(217,217,217)' }}
                ></div>
                <NewFolder />
              </ButtonGroup>
            </ControlsRow>
            <div
              style={{ height: '1px', width: '100%', background: 'rgb(217,217,217)' }}
            ></div>
            <ControlsRow style={{ marginTop: 20 }}>
              {(sortedFolders.length > 0 ||
                (presentations && presentations.length > 0)) && (
                <SortingControls
                  enableSearch
                  onSearchChange={e => setSearch(e)}
                  searchValue={search}
                  onChange={handleChangeSortCondition}
                  sortCondition={sortCondition}
                />
              )}
            </ControlsRow>
          </HeaderContainer>
          {isSearching && debouncedSearchQuery && (
            <div>
              <SearchGrid search={debouncedSearchQuery} />
            </div>
          )}

          {sortedFolders.length > 0 && !isSearching && (
            <div>
              <Title $fontSize={16}>
                <T keyName="folders" defaultValue="Folders" />
              </Title>
              {isFetchingFolders && allFolders === undefined && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 50,
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 20, color: theme.colors.black }}
                        spin
                      />
                    }
                  />
                </div>
              )}
              <FoldersSection
                folders={sortedFolders}
                isDeletingFolder={isDeletingFolder || false}
                deleteFolder={deleteFolder}
                allFolders={allFolders}
                saveFolder={saveFolder}
                isSavingFolder={isSavingFolder}
                type="lessons"
              />
            </div>
          )}
          {presentations && presentations.length > 0 && !isSearching && (
            <div>
              <Title $fontSize={16}>
                <T keyName="lessons" defaultValue="Lessons" />
              </Title>
              <LessonsSection
                lessons={presentations}
                presentationsCacheKey={presentationsCacheKey}
              />
            </div>
          )}
          {presentations !== undefined &&
            presentations.length === 0 &&
            allFolders !== undefined &&
            sortedFolders.length === 0 &&
            !isLoadingData && (
              <div>
                <EmptyStateContainer $isGeneratorRow={true}>
                  <LiveEmptyState
                    maxWidth={500}
                    icon={<CuriBookmarksSvg />}
                    title={t('addLesson', 'Add a lesson to get started')}
                  >
                    <CreateButton
                      onClick={() => {
                        metrics.logEvent('ControlsRow.GetLessonClicked', {
                          from: 'folderPage',
                        });
                        router.push(`/${tenantId}/home`);
                      }}
                      style={{ width: 200 }}
                      icon={<PlusSvg />}
                    >
                      <T keyName="Get lesson" defaultValue="Get lesson" />
                      <span
                        style={{
                          fontSize: 12,
                          color: theme.colors.orange,
                          background: 'rgba(255,255,255,0.8)',
                          borderRadius: 7,
                          padding: '0 8px',
                          marginLeft: 10,
                        }}
                      >
                        <T keyName="AI" defaultValue="AI" />
                      </span>
                    </CreateButton>
                  </LiveEmptyState>
                </EmptyStateContainer>
              </div>
            )}

          {!isSearching && (
            <div ref={fetchMorePresentationsRef}>
              {isFetchingNextPage && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 50,
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 20, color: theme.colors.black }}
                        spin
                      />
                    }
                  />
                </div>
              )}
            </div>
          )}
          <div style={{ height: 20, width: '100%' }}>
            {/* Render this empty div to make sure the inView div triggers fetch more  */}
          </div>
        </ContentContainer>
      </PageContainer>
      <OnboardingModal
        open={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
      />
    </>
  );
};

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 800px) {
    justify-content: center;
    flex-wrap: wrap;
  }
  margin-bottom: 20px;
`;

export const CreateButton = styled(AntButton)`
  background: ${theme.colors.orangeGradient};
  height: 42px;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  outline: none;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border: none;
  border-radius: 5px;
  font-family: 'Nunito';
  font-size: 16px;
  font-style: normal;
  font-weight: bold;
  line-height: 22px;
  padding: 11.5px 16px;

  /** Disabling Animation on Click */
  &[ant-click-animating-without-extra-node]:after {
    animation: none !important;

    -moz-animation: none !important;
    -ms-animation: none !important;
    -o-animation: none !important;
    -webkit-animation: none !important;
  }

  svg {
    margin-right: 10px;
    width: 15px;
    height: 15px;
  }

  &:focus {
    background: linear-gradient(90deg, #ffa55b 59.9%, #ffd042 100%);
    color: ${theme.colors.white};
    border: none;
  }

  &:hover {
    transition: all 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);
    background: linear-gradient(90deg, #ffa55b 59.9%, #ffd042 100%);
    color: ${theme.colors.white};
    border: none;
  }

  &:active {
    background: linear-gradient(90deg, #ffa55b 59.9%, #ffd042 100%);
    color: ${theme.colors.white};
    border: none;
  }
`;

const EmptyStateContainer = styled.div<{ $isGeneratorRow?: boolean }>`
  div > svg {
    height: 150px !important;
    width: 200px !important;
  }

  ${({ $isGeneratorRow }) =>
    $isGeneratorRow &&
    css`
      padding: 0px 20px;
      > div {
        margin-top: 0px;
        padding: 10px 0px;
        min-height: fit-content;
        div > svg {
          height: 70px !important;
          width: 100px !important;
        }
        p {
          margin: 0px;
        }
        h3 {
          margin-top: 5px !important;
        }
      }
    `}

  @media (max-width: 800px) {
    div > svg {
      height: 100px !important;
      width: 150px !important;
    }
  }
`;

const HeaderContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.black};
  margin: 0 0 0px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const PageContainer = styled.div`
  background-color: ${theme.colors.background};
  height: inherit;
  width: 100%;
  height: 100%;

  @media (max-width: 900px) {
    padding-top: 60px;
  }
`;

const ContentContainer = styled.div<{
  $isDesktop?: boolean;
}>`
  width: 100%;
  height: 100%;
  padding-bottom: 20px;

  ${({ $isDesktop }) =>
    $isDesktop &&
    css`
      padding-left: 240px;
    `}
  ${({ $isDesktop }) =>
    !$isDesktop &&
    css`
      padding-left: 0px;
    `}
`;
